// src/admin/screens/login/LoginScreen.tsx
// First-step sign-in: email + password. After auth, branches to TOTP challenge
// (existing factor) or TOTP enrolment (first login). Visual treatment matches
// the admin design: three-bar logo, "Admin Console" eyebrow, soft cobalt-tinted
// gradient page background.
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState } from 'react';
import { supabase } from '@admin/lib/supabase';
import ThandizoLogo from '@admin/components/ThandizoLogo';
import styles from './Login.module.css';

const schema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(1, 'Required'),
});
type FormValues = z.infer<typeof schema>;

export default function LoginScreen() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const preloadedError = params.get('error');
  const resetOk = params.get('reset') === 'ok';

  const { register, handleSubmit, formState: { errors, isSubmitting } } =
    useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = async (values: FormValues) => {
    setSubmitError(null);
    const { data, error } = await supabase.auth.signInWithPassword(values);
    if (error) { setSubmitError(error.message); return; }
    const { data: factors } = await supabase.auth.mfa.listFactors();
    const totp = factors?.totp ?? [];
    if (totp.length > 0 && totp.some((f) => f.status === 'verified')) {
      navigate(`/login/totp?next=${encodeURIComponent(params.get('next') ?? '/')}`, { replace: true });
    } else {
      navigate('/login/enrol-totp', { replace: true });
    }
    void data;
  };

  return (
    <div className={styles.page}>
      <div className={styles.shell}>
        <div className={styles.brandRow}>
          <ThandizoLogo size={48} />
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <span className={styles.brandName}>Thandizo</span>
            <span className={styles.brandSub}>Admin Console</span>
          </div>
        </div>

        <form className={styles.card} onSubmit={handleSubmit(onSubmit)} noValidate>
          <h2>Sign in</h2>
          <p className={styles.lead}>Use your staff email and password</p>

          {preloadedError && <div className={styles.error}>Not authorized. Contact a super admin.</div>}
          {resetOk && <div className={styles.info}>Password updated. Sign in with your new password.</div>}

          <label className={styles.field}>
            Email
            <input type="email" autoFocus {...register('email')} />
            {errors.email && <span className={styles.fieldError}>{errors.email.message}</span>}
          </label>

          <label className={styles.field}>
            Password
            <input type="password" {...register('password')} />
            {errors.password && <span className={styles.fieldError}>{errors.password.message}</span>}
          </label>

          {submitError && <div className={styles.error}>{submitError}</div>}

          <button type="submit" className={styles.submit} disabled={isSubmitting}>
            {isSubmitting ? 'Signing in…' : 'Continue'}
          </button>
        </form>

        <div className={styles.foot}>
          Session limited to 8 hours · Protected by Cloudflare Access<br />
          Never share your credentials · <a href="mailto:david@thandizo.co.zm">Report a problem</a>
        </div>
      </div>
    </div>
  );
}
